<template>
  <div class="d-flex justify-center">
    <v-text-field
      :model-value="trendOverrideDisplay"
      :disabled="!canEditModellingPage || isProductModellingJobRunning"
      class="mr-2"
      width="140px"
      :placeholder="tkey('enterTrend')"
      :rules="trendRules"
      hide-details
      @update:model-value="onTrendOverrideChange"
    />
    <v-btn primary :disabled="isTrendDisabled || isProductModellingJobRunning" @click="applyTrend">
      {{ tkey('actions.applyToEditable') }}
    </v-btn>
  </div>
</template>

<script setup>
import { ref, computed, defineEmits } from 'vue';
import { isEmpty, every, map } from 'lodash';

import numberUtils from '@/utils/number-format-utils';

import { useInternationalization } from '@/composables/use-internationalization';
import { usePermission } from '@/composables/use-permission';
import { useInputValidations } from '@/composables/input-validations';
import { useFormatNumber } from '@/composables/use-format-number';

import { useScenariosStore } from '@/stores/scenarios';

const { tkey } = useInternationalization('productModellingPage');
const { canEditModellingPage } = usePermission();
const { isNumber, isGreaterOrEqual } = useInputValidations();
const { formatNumber, formatStringToNumber } = useFormatNumber();
const scenariosStore = useScenariosStore();

const props = defineProps({ minTrendValue: Number });
const emit = defineEmits(['apply-trend']);

const trendOverrideDisplay = ref(null);
const trendRules = [isNumber, v => isGreaterOrEqual(v, props.minTrendValue)];

const trendOverride = computed(() => {
  return !isEmpty(trendOverrideDisplay.value)
    ? numberUtils.formatStringToNumber(trendOverrideDisplay.value)
    : trendOverrideDisplay.value;
});

const isTrendDisabled = computed(() => {
  if (!canEditModellingPage) return true;

  if (!numberUtils.validNumericString(trendOverrideDisplay.value)) {
    return true;
  }

  return (
    !every(
      map(trendRules, r => r(trendOverride)),
      v => !!v
    ) || trendOverride.value < props.minTrendValue
  );
});

const isProductModellingJobRunning = computed(() => {
  return scenariosStore.isJobRunning('setupProductModelling');
});

const sanitizeTrendInputValue = trendOverrideDisplayValue => {
  if (!numberUtils.validNumericString(trendOverrideDisplayValue)) {
    return trendOverrideDisplayValue;
  }
  return !isEmpty(trendOverrideDisplayValue)
    ? formatStringToNumber(trendOverrideDisplayValue)
    : trendOverrideDisplayValue;
};

const onTrendOverrideChange = value => {
  const sanitizedValue = sanitizeTrendInputValue(value);

  if (!numberUtils.validNumericString(sanitizedValue)) {
    trendOverrideDisplay.value = sanitizedValue;
    return;
  }

  trendOverrideDisplay.value = !isEmpty(String(sanitizedValue))
    ? formatNumber({
        number: sanitizedValue,
        format: 'float',
      })
    : '';
};

const applyTrend = () => {
  emit('apply-trend', trendOverride.value);
};
</script>
